import React, { useEffect, useMemo, useState } from 'react';

import {
  AttachmentsIcon,
  BadFileAttachmentView,
  ChakraButton,
  ChakraFlex,
  ChakraImage,
  ChakraInput,
  ChakraInputGroup,
  ChakraInputRightElement,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FileIcons,
  Popover,
  Search,
  SkeletonLoader,
} from '@equip.health/ui';
import { FileMessage } from '@sendbird/chat/message';

import { useNoteAttachmentModalContext } from '~/lib/contexts/NoteAttachmentModalContext';
import useChatAttachment from '~/lib/hooks/chat/useChatAttachment';
import { ChatMessage } from '~/lib/types/chat';
import {
  filterMessages,
  getFileIcon,
  isImage,
} from '~/lib/util/chat/chat.utils';

import MessageKebabMenu from './MessageKebabMenu';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

const idPrefix = 'channel';
const idPostfix = 'attachments';

const { caption, label, h6 } = EquipUITheme.typography;
const { neutral, primary } = EquipUIFireflyV2Theme.colors;

interface AttachmentItemProps {
  displayName: string;
  id: number;
  isChannelReadOnly: boolean;
  isImageAttachment: boolean;
  isThreadedMessage: boolean;
  name: string;
  onMessageRemoved: (isRemoved: boolean) => void;
  sender: string;
  sentDate: string;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({
  displayName,
  id,
  isChannelReadOnly,
  isImageAttachment,
  isThreadedMessage,
  name,
  onMessageRemoved,
  sender,
  sentDate,
}: AttachmentItemProps) => {
  const {
    fileValidationStatus,
    getDocumentDownloadURL,
    isFileValid,
    removeAttachment,
    s3Url,
  } = useChatAttachment();
  const { currentPatientId } = useGlobalContext();

  const [isDocumentExists, setIsDocumentExists] = useState<boolean>(true);

  const { setShowNoteAttachmentModal } = useNoteAttachmentModalContext();
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);

  useEffect(() => {
    getDocumentDownloadURL({
      fileName: name,
      patientExternalId: currentPatientId,
      onSuccess: () => {
        setIsDocumentExists(true);
      },
      onFailure: () => {
        setIsDocumentExists(false);
      },
    });
  }, []);

  const handleRemoveMessage = () => {
    removeAttachment(
      name,
      () => {
        setIsDocumentExists(false);
        onMessageRemoved(true);
      },
      () => {
        setIsDocumentExists(true);
        onMessageRemoved(false);
      },
    );
  };

  const isValidImage = useMemo(
    () => isImageAttachment && isFileValid,
    [isImageAttachment, isFileValid],
  );

  const isValidDocument = useMemo(
    () => !isImageAttachment && isFileValid,
    [isImageAttachment, isFileValid],
  );

  const isInvalidAttachment = useMemo(
    () => !isValidImage && !isValidDocument,
    [isValidImage, isValidDocument],
  );

  if (!isDocumentExists) return null;

  if (!s3Url && isImageAttachment && fileValidationStatus !== 'INVALID') {
    return <SkeletonLoader boxSize="58px" id="attachment-item__skeleton" />;
  }

  function refetchAttachmentStatus() {
    getDocumentDownloadURL({
      fileName: name,
      patientExternalId: currentPatientId,
      onSuccess: () => {
        if (s3Url) {
          setShowNoteAttachmentModal({
            fileValidationStatus,
            name,
            url: s3Url,
          });
          setIsDocumentExists(true);
        } else {
          setIsDocumentExists(false);
        }
      },
      onFailure: () => {
        setIsDocumentExists(false);
      },
    });
  }
  return (
    <ChakraFlex
      alignItems="center"
      backgroundColor={isHighlighted ? 'neutral.background.secondary' : 'white'}
      borderRadius="16px"
      justifyContent="space-between"
      onMouseEnter={() => setIsHighlighted(true)}
      onMouseLeave={() => setIsHighlighted(false)}
      width="100%"
    >
      <ChakraFlex
        cursor="pointer"
        gridGap="8px"
        onClick={() => {
          if (fileValidationStatus === 'NEW') {
            refetchAttachmentStatus();
          } else if (s3Url && isFileValid) {
            setShowNoteAttachmentModal({
              fileValidationStatus,
              name,
              url: s3Url,
            });
          }
        }}
        title={displayName}
      >
        {isValidImage && (
          <ChakraImage
            borderRadius="10px"
            height="58px"
            loading="lazy"
            objectFit="cover"
            src={s3Url}
            width="58px"
          />
        )}

        {isValidDocument && (
          <FileIcons height="58px" icon={getFileIcon(name)} width="58px" />
        )}

        {isInvalidAttachment && <BadFileAttachmentView />}

        <ChakraFlex direction="column">
          <ChakraText
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            width="200px"
            {...label}
            id={`${idPrefix}-attachment-name__${id.toFixed()}__${idPostfix}`}
          >
            {displayName}
          </ChakraText>
          <ChakraText
            color="neutral.secondary"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            width="200px"
            {...caption}
            id={`${idPrefix}-attachment-sender-and-date__${id.toFixed()}__${idPostfix}`}
          >
            {sender && `Sent by ${sender} on ${sentDate}`}
          </ChakraText>
        </ChakraFlex>
      </ChakraFlex>

      {!isThreadedMessage && !isChannelReadOnly && (
        <MessageKebabMenu
          messageId={id}
          onRemoveClicked={handleRemoveMessage}
        />
      )}
    </ChakraFlex>
  );
};

export interface ChannelAttachmentsProps {
  attachments: ChatMessage[];
  id: string;
  isChannelReadOnly: boolean;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
}

const AttachmentsList: React.FC<ChannelAttachmentsProps> = ({
  attachments,
  id,
  isChannelReadOnly,
  onMessageRemoved,
}: ChannelAttachmentsProps) => {
  return (
    <ChakraFlex
      direction="column"
      gridGap="16px"
      id={`${idPostfix}-list__${id}__container`}
    >
      {(attachments ?? []).map((attachment) => {
        const { relativeDate, sbMessage, message, hasThread } = attachment;
        const fileMessage = sbMessage as FileMessage;
        const { name, messageId, parentMessageId, sender } = fileMessage;
        return (
          <AttachmentItem
            displayName={message}
            id={messageId}
            isChannelReadOnly={isChannelReadOnly}
            isImageAttachment={isImage(name)}
            isThreadedMessage={hasThread}
            key={messageId}
            name={name}
            onMessageRemoved={(isRemoved) => {
              if (isRemoved)
                onMessageRemoved(
                  parentMessageId === 0 ? messageId : parentMessageId,
                  parentMessageId === 0 ? null : messageId,
                );
            }}
            sender={sender.nickname}
            sentDate={relativeDate}
          />
        );
      })}
    </ChakraFlex>
  );
};

const ChannelAttachments: React.FC<ChannelAttachmentsProps> = ({
  attachments,
  id,
  isChannelReadOnly,
  onMessageRemoved,
}: ChannelAttachmentsProps) => {
  const [showUserPopover, setShowUserPopover] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  return (
    <Popover
      bodyContent={
        attachments?.length === 0 ? (
          'No media found'
        ) : (
          <AttachmentsList
            attachments={filterMessages(attachments, searchText)}
            id={id}
            isChannelReadOnly={isChannelReadOnly}
            onMessageRemoved={onMessageRemoved}
          />
        )
      }
      headerContent={
        <ChakraFlex
          direction="column"
          gridGap="16px"
          id={`${idPrefix}-channel-media-container__${id}__${idPostfix}`}
        >
          <ChakraText
            color="neutral.primary"
            {...h6}
            id={`${idPrefix}-channel-media-label__${id}__${idPostfix}`}
          >
            Channel Media
          </ChakraText>
          <ChakraInputGroup>
            <ChakraInput
              disabled={attachments?.length === 0}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search here ..."
              value={searchText}
            />
            <ChakraInputRightElement>
              <Search
                fill={
                  attachments?.length === 0 ? neutral.secondary : primary[100]
                }
                height="16px"
              />
            </ChakraInputRightElement>
          </ChakraInputGroup>
        </ChakraFlex>
      }
      id={id}
      isOpen={showUserPopover}
      onClose={() => setShowUserPopover(false)}
      onOpen={() => setShowUserPopover(true)}
      size="md"
      triggerContent={
        <ChakraButton backgroundColor="primary.10" padding="0">
          <AttachmentsIcon fill={primary[100]} />
        </ChakraButton>
      }
    />
  );
};

export default ChannelAttachments;
